import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import { TextField, Loading } from "components/form";
import Modal from "components/Modal";
import { useCore } from "store/core";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function Edit({
  show,
  item,
  submit,
  close,
  title = "ویرایش نقش",
}) {
  const core = useCore();

  // استخراج abilities از permissions
  const extractAbilities = (permissions = []) =>
    permissions.flatMap((perm) =>
      perm.actions.map((action) => `${perm.resource}.${action}`)
    );

  // باز کردن گروه‌هایی که permission دارند
  const getInitialOpenGroups = () => {
    const result = {};
    (item?.permissions || []).forEach((perm) => {
      result[perm.resource] = true;
    });
    return result;
  };

  const [openGroups, setOpenGroups] = useState(getInitialOpenGroups);

  useEffect(() => {
    if (!item || !item.permissions) return;

    const initialOpen = {};
    item.permissions.forEach((perm) => {
      initialOpen[perm.resource] = true;
    });
    setOpenGroups(initialOpen);
  }, [item]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const initialValues = {
    name: item?.name || "",
    faName: item?.faName || "",
    abilities: extractAbilities(item?.permissions || []),
  };

  return (
    <Modal show={show} handleClose={close} title={title}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={async (values, { setSubmitting }) => {
          const groupedPermissions = values.abilities.reduce((acc, item) => {
            const [resource, action] = item.split(".");
            if (!acc[resource]) acc[resource] = [];
            acc[resource].push(action);
            return acc;
          }, {});

          const permissions = Object.entries(groupedPermissions).map(
            ([resource, actions]) => ({
              resource,
              actions: [...new Set(actions)],
            })
          );

          const payload = {
            name: values.name,
            faName: values.faName,
            permissions,
          };

          await submit(payload, item.id);
          close();
          setSubmitting(false);
        }}
      >
        {({ isSubmitting, dirty, values, setFieldValue }) => (
          <Form>
            <div className="modal-body gap-y-3">
              <div className="intro-x grid md:grid-cols-2 md:gap-3">
                <Field
                  placeholder="نام انگلیسی (مثلاً admin)"
                  label="عنوان انگلیسی :"
                  name="name"
                  component={TextField}
                />
                <Field
                  placeholder="مثلاً مدیر سیستم"
                  label="عنوان فارسی :"
                  name="faName"
                  component={TextField}
                />
              </div>

              <div className="mt-4 space-y-3">
                {(core.permissions || []).map((perm) => {
                  const groupKey = perm.resource;
                  const isOpen = openGroups[groupKey];

                  return (
                    <div
                      key={groupKey}
                      className="border rounded bg-gray-50 shadow-sm"
                    >
                      <div
                        className="cursor-pointer px-4 py-3 flex justify-between items-center hover:bg-gray-100"
                        onClick={() => toggleGroup(groupKey)}
                      >
                        <h3 className="font-semibold text-sm text-gray-700">
                          {perm.label}
                        </h3>
                        {isOpen ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </div>

                      {isOpen && (
                        <div className="px-4 py-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {perm.actions.map((action) => {
                            const val = `${perm.resource}.${action.key}`;
                            const checked = values.abilities.includes(val);

                            return (
                              <label
                                key={val}
                                className="flex items-center gap-2"
                              >
                                <input
                                  type="checkbox"
                                  className="accent-blue-600"
                                  checked={checked}
                                  onChange={() => {
                                    const current = [...values.abilities];
                                    if (checked) {
                                      setFieldValue(
                                        "abilities",
                                        current.filter((v) => v !== val)
                                      );
                                    } else {
                                      setFieldValue("abilities", [
                                        ...current,
                                        val,
                                      ]);
                                    }
                                  }}
                                />
                                <span className="text-sm text-gray-700">
                                  {action.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer text-right">
              <button
                className="btn btn-primary w-20"
                type="submit"
                disabled={isSubmitting || !dirty}
              >
                {isSubmitting ? <Loading /> : "ذخیره"}
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={close}
                className="btn btn-outline-secondary w-20 mr-1"
              >
                انصراف
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
